import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Building2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n";

const PHONE_RE = /^[6-9]\d{9}$/;
const BUDGET_VALUES = ["under-10", "10-20", "20-40", "40-plus"];

const EMPTY = { name: "", phone: "", email: "", city: "", budget: "10-20", message: "", needs_finance: false };

const Franchise = () => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!PHONE_RE.test(form.phone.replace(/[\s-]/g, ""))) {
      toast.error(t.error_phone);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/franchise-enquiries", form);
      toast.success(t.fr_success_title, { description: t.fr_success_body });
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-zinc-700 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-200 focus:border-volt focus:ring-1 focus:ring-volt";
  const labelCls = "mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-widest text-zinc-400";

  return (
    <section id="franchise" className="relative border-t border-zinc-800/60 bg-surface/40 py-24 sm:py-32" data-testid="franchise-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt" data-testid="franchise-kicker">
            — {t.franchise_kicker}
          </p>
          <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="franchise-title">
            {t.franchise_title}
          </h2>
          <p className="mt-4 max-w-md text-base text-zinc-400 sm:text-lg" data-testid="franchise-subtitle">{t.franchise_sub}</p>
          <ul className="mt-8 space-y-4">
            {t.franchise_points.map((point, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-300 sm:text-base" data-testid={`franchise-point-${i}`}>
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-volt" />
                {point}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="rounded-2xl border border-volt/30 bg-panel p-7 volt-glow sm:p-9"
        >
          <h3 className="flex items-center gap-2.5 font-display text-2xl font-extrabold uppercase tracking-wide text-white" data-testid="franchise-form-title">
            <Building2 className="h-6 w-6 text-volt" />
            {t.franchise_form_title}
          </h3>
          <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="franchise-form">
            <div>
              <label className={labelCls} htmlFor="fr-name">{t.form_name} *</label>
              <input id="fr-name" data-testid="franchise-name-input" required minLength={2} maxLength={80}
                className={inputCls} value={form.name} onChange={set("name")} placeholder="Rahul Sharma" />
            </div>
            <div>
              <label className={labelCls} htmlFor="fr-phone">{t.form_phone} *</label>
              <input id="fr-phone" data-testid="franchise-phone-input" required
                className={inputCls} value={form.phone} onChange={set("phone")} placeholder="98XXXXXXXX" inputMode="numeric" />
            </div>
            <div>
              <label className={labelCls} htmlFor="fr-email">{t.fr_email}</label>
              <input id="fr-email" data-testid="franchise-email-input" type="email"
                className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div>
              <label className={labelCls} htmlFor="fr-city">{t.fr_city} *</label>
              <input id="fr-city" data-testid="franchise-city-input" required minLength={2} maxLength={80}
                className={inputCls} value={form.city} onChange={set("city")} placeholder="Asansol" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="fr-budget">{t.fr_budget} *</label>
              <select id="fr-budget" data-testid="franchise-budget-select" className={inputCls} value={form.budget} onChange={set("budget")}>
                {BUDGET_VALUES.map((v, i) => (
                  <option key={v} value={v} data-testid={`franchise-budget-option-${v}`}>{t.fr_budgets[i]}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="fr-message">{t.fr_message}</label>
              <textarea id="fr-message" data-testid="franchise-message-input" rows={3}
                className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="..." />
            </div>
            <div className="sm:col-span-2">
              <button
                type="button"
                data-testid="franchise-finance-toggle"
                onClick={() => setForm((f) => ({ ...f, needs_finance: !f.needs_finance }))}
                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors duration-200 ${
                  form.needs_finance ? "border-volt bg-volt/10" : "border-zinc-700 hover:border-zinc-500"
                }`}
              >
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${form.needs_finance ? "border-volt bg-volt" : "border-zinc-600"}`}>
                  {form.needs_finance && <CheckCircle2 className="h-3.5 w-3.5 text-ink" />}
                </span>
                <span className={`text-sm font-semibold ${form.needs_finance ? "text-volt" : "text-zinc-300"}`}>{t.fr_finance}</span>
              </button>
            </div>
            <button
              data-testid="franchise-submit-button"
              type="submit"
              disabled={submitting}
              className="flex items-center justify-center gap-2 rounded-full bg-volt py-4 font-display text-base font-bold uppercase tracking-widest text-ink transition-all duration-200 hover:bg-volt-hover hover:scale-[1.01] disabled:opacity-60 sm:col-span-2"
            >
              {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
              {submitting ? t.fr_submitting : t.fr_submit}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default Franchise;
